/**
 * @fileoverview API service for deepcut conversation analysis
 */

import TrebbleClientAPIHelper from "../../../models/helper/TrebbleClientAPI";
import PusherHelper from "../../../models/helper/PusherHelper";
// @ts-ignore
import ti18n from 'i18n!nls/Deepcuti18n';
/**
 * @typedef {import('../types/deepcut.js').TranscriptAnalysisRequest} TranscriptAnalysisRequest
 * @typedef {import('../types/deepcut.js').AnalysisResults} AnalysisResults
 * @typedef {import('../types/deepcut.js').UserFeedback} UserFeedback
 */

/**
 * DeepCut API service for transcript analysis
 */
class DeepCutApiService {
  constructor() {
    // Get TrebbleClientAPI instance
    this.trebbleApi = null;
    this._initializeTrebbleApi();
    // Track the current session's reference request ID
    this._currentReferenceRequestUID = null;
    // Track processed notification IDs to avoid duplicate API calls
    this._processedNotificationIds = new Set();
  }

  /**
   * Initialize TrebbleClientAPI instance
   */
  _initializeTrebbleApi() {
    // Access TrebbleClientAPI from global scope
    if (window.trebble && window.trebble.helper && window.trebble.helper.TrebbleClientAPI) {
      this.trebbleApi = window.trebble.helper.TrebbleClientAPI;
    } else {
      console.warn('TrebbleClientAPI not found in global scope. Will retry on first request.');
    }
  }

  /**
   * Sets the reference request UID for the current session
   * @param {string|null} referenceRequestUID - The reference request UID to track subsequent requests
   */
  setReferenceRequestUID(referenceRequestUID) {
    console.log('DeepCut API: Setting referenceRequestUID to:', referenceRequestUID);
    this._currentReferenceRequestUID = referenceRequestUID;
  }

  /**
   * Gets the current reference request UID
   * @returns {string|null} The current reference request UID
   */
  getReferenceRequestUID() {
    return this._currentReferenceRequestUID;
  }

  /**
   * Clears the current reference request UID (typically when starting a new session)
   */
  clearReferenceRequestUID() {
    console.log('DeepCut API: Clearing referenceRequestUID');
    this._currentReferenceRequestUID = null;
  }

  /**
   * Gets the current session information including reference request UID
   * @returns {Object} Session information
   */
  getCurrentSessionInfo() {
    return {
      referenceRequestUID: this._currentReferenceRequestUID,
      hasReferenceRequestUID: !!this._currentReferenceRequestUID
    };
  }


  /**
   * Validates analysis request data
   * @param {TranscriptAnalysisRequest} requestData - Request data to validate
   * @throws {Error} If validation fails
   */
  _validateAnalysisRequest(requestData) {
    if (!requestData) {
      throw new Error('Request data is required');
    }

    if (!requestData.transcript || typeof requestData.transcript !== 'string') {
      throw new Error('Valid transcript text is required');
    }

    if (requestData.transcript.trim().length === 0) {
      throw new Error('Transcript cannot be empty');
    }

    if (!Array.isArray(requestData.userDirectives)) {
      throw new Error('userDirectives must be an array');
    }
  }

  /**
   * Transforms request data to match backend expectations
   * @param {TranscriptAnalysisRequest} requestData - Original request data
   * @returns {Object} Transformed data for backend
   */
  _transformRequestData(requestData) {
    // Convert question answers from array format to object format if needed
    let probingQuestionAnswers = {};
    if (requestData.questionAnswers && Array.isArray(requestData.questionAnswers)) {
      requestData.questionAnswers.forEach(qa => {
        if (qa.questionId && qa.answer) {
          probingQuestionAnswers[qa.questionId] = qa.answer;
        }
      });
    } else if (requestData.probingQuestionAnswers) {
      probingQuestionAnswers = requestData.probingQuestionAnswers;
    }

    return {
      transcript: requestData.transcript,
      transcriptionId: requestData.transcriptionId || null,
      projectId: requestData.projectId || null,
      selectedIntent: requestData.selectedIntent || '',
      userDirectives: requestData.userDirectives || [],
      platformType: requestData.targetPlatform || 'web',
      contentType: requestData.selectedContentType || requestData.contentType || 'conversation',
      customDirectives: requestData.customDirectives || '',
      probingQuestionAnswers: probingQuestionAnswers,
      userFlow: requestData.userFlow || {},
      // Edit Like Me fields
      referenceProjectId: requestData.referenceProjectId || null,
      isEditLikeMeMode: requestData.isEditLikeMeMode || false,
      // Locale for language-aware processing
      locale: requestData.locale || navigator.language || 'en'
    };
  }

  /**
   * Waits for request completion using Pusher notifications and a status polling fallback
   * @param {string} requestId - The request ID to wait for
   * @returns {Promise<Object>} The completed result
   * @private
   */
  async _pollForCompletion(requestId) {
    const api = TrebbleClientAPIHelper.getInstance();
    const pusherHelper = PusherHelper.getInstance();

    console.log('DeepCut API: Waiting for request completion via Pusher notification or status poll:', requestId);

    // Store cleanup function in closure variable (can't attach to Promise with older libraries)
    let pusherCleanup = null;
    const TIMEOUT_MS = 2 * 60 * 1000; // 2 minutes safety timeout
    const POLL_INTERVAL_MS = 5000;
    const REFERENCE_PATTERNS_FETCH_TIMEOUT_MS = 15000;

    // Create a promise that resolves when either Pusher or the status endpoint reports completion
    const pusherPromise = new Promise((resolve, reject) => {
      let pusherListenerAdded = false;
      let timeoutId = null;
      let pollIntervalId = null;
      let pollInFlight = false;
      let settled = false;
      let handlePusherNotification = null;

      const cleanup = () => {
        if (pusherListenerAdded && handlePusherNotification) {
          pusherHelper.off(pusherHelper.eventTypes.WEB_PUSH_NOTIFICATION, handlePusherNotification);
          pusherListenerAdded = false;
        }
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        if (pollIntervalId) {
          clearInterval(pollIntervalId);
          pollIntervalId = null;
        }
      };

      const isTransientError = (error) => {
        return error?.message && (
          error.message.toLowerCase().includes('network') ||
          error.message.toLowerCase().includes('timeout') ||
          error.message.toLowerCase().includes('fetch')
        );
      };

      const fetchReferencePatterns = async (patternsUrl) => {
        let patternsTimeoutId = null;
        const timeoutPromise = new Promise((_, reject) => {
          patternsTimeoutId = setTimeout(() => {
            reject(new Error('Reference editing patterns fetch timed out'));
          }, REFERENCE_PATTERNS_FETCH_TIMEOUT_MS);
        });
        const fetchPromise = (async () => {
          const patternsResponse = await fetch(patternsUrl);
          if (!patternsResponse.ok) {
            return null;
          }
          return patternsResponse.json();
        })();

        try {
          return await Promise.race([fetchPromise, timeoutPromise]);
        } finally {
          if (patternsTimeoutId) {
            clearTimeout(patternsTimeoutId);
          }
        }
      };

      const buildCompletedResults = async (statusResult, sourceLabel) => {
        // Store referenceRequestUID if present
        if (statusResult.referenceRequestUID) {
          this.setReferenceRequestUID(statusResult.referenceRequestUID);
        }

        // Fetch reference editing patterns from S3 if URL is available
        let results = statusResult.analysisResults || statusResult;
        // Preserve the URL for downstream use
        if (statusResult.referenceEditingPatternsUrl && typeof results === 'object') {
          results.referenceEditingPatternsUrl = statusResult.referenceEditingPatternsUrl;
          console.log('DeepCut API: Attached referenceEditingPatternsUrl:', statusResult.referenceEditingPatternsUrl);
        }
        if (statusResult.referenceEditingPatternsUrl && !results.referenceEditingPatterns) {
          try {
            const patternsData = await fetchReferencePatterns(statusResult.referenceEditingPatternsUrl);
            if (patternsData) {
              results.referenceEditingPatterns = patternsData.patterns || patternsData.referenceEditingPatterns || patternsData;
            }
          } catch (patternFetchError) {
            console.warn(`DeepCut API: Failed to fetch referenceEditingPatterns from S3 after ${sourceLabel}:`, patternFetchError.message);
          }
        }

        return results;
      };

      const rejectOnce = (error) => {
        if (settled) {
          return;
        }
        settled = true;
        cleanup();
        reject(error);
      };

      const handleStatusResult = async (statusResult, sourceLabel) => {
        if (settled || !statusResult) {
          return;
        }

        if (statusResult.status === 'COMPLETED') {
          console.log(`DeepCut API: Request completed via ${sourceLabel}`);
          settled = true;
          cleanup();
          try {
            const results = await buildCompletedResults(statusResult, sourceLabel);
            resolve(results);
          } catch (error) {
            reject(error);
          }
        } else if (statusResult.status === 'FAILED') {
          console.error(`DeepCut API: Request failed via ${sourceLabel}:`, statusResult.errorDetails);
          rejectOnce(new Error(statusResult.errorDetails?.message || window.getI18n(ti18n, 'ANALYSIS_FAILED')));
        }
      };

      const checkRequestStatus = async (sourceLabel, rejectHardErrors) => {
        try {
          const statusResult = await api.getDeepCutRequestStatus(requestId);
          await handleStatusResult(statusResult, sourceLabel);
        } catch (error) {
          if (settled) {
            return;
          }
          console.error(`DeepCut API: Error fetching status via ${sourceLabel}:`, error);

          if (rejectHardErrors && !isTransientError(error)) {
            rejectOnce(error);
          }
        }
      };

      // Safety timeout in case Pusher and polling both fail
      timeoutId = setTimeout(() => {
        console.warn('DeepCut API: Completion wait timed out after 2 minutes');
        rejectOnce(new Error(window.getI18n(ti18n, 'TIMEOUT_ERROR')));
      }, TIMEOUT_MS);

      handlePusherNotification = async (data) => {
        console.log('DeepCut API: Received Pusher notification:', data);

        // Check if this notification is a DeepCut notification
        if (data.notificationType === 'deepcutAnalysisCompleted' || data.notificationType === 'deepcutAnalysisFailed') {
          // Skip already-processed notifications (Pusher queues old ones on reconnect)
          if (this._processedNotificationIds.has(data.notificationId)) {
            console.log('DeepCut API: Notification already processed, skipping:', data.notificationId);
            return;
          }
          this._processedNotificationIds.add(data.notificationId);

          try {
            // Fetch the full notification payload from server to get requestId, projectId, and transcriptionId
            const notificationPayload = await api.getNotificationCustomPayloadByNotificationId(data.notificationId);
            const notificationData = notificationPayload && notificationPayload.notificationData;
            const notificationRequestId = notificationData && notificationData.requestId;

            // Extract projectId and transcriptionId (priority: projectId > transcriptionId)
            const projectId = notificationData && notificationData.projectId;
            const transcriptionId = notificationData && notificationData.transcriptionId;
            const contextId = projectId || transcriptionId; // Use projectId if available, else transcriptionId

            console.log('DeepCut API: Notification context - projectId:', projectId, 'transcriptionId:', transcriptionId, 'using:', contextId);

            // Check if this notification is for the request we're waiting for
            if (notificationRequestId !== requestId) {
              console.log('DeepCut API: Notification is for different request, ignoring');
              return;
            }

            console.log('DeepCut API: Request status changed via Pusher notification, fetching results');
            await checkRequestStatus('Pusher notification', true);
          } catch (error) {
            console.error('DeepCut API: Error handling Pusher notification:', error);

            // Don't reject on transient errors - wait for the status poll or timeout
            // Only reject for hard failures that indicate permanent issues
            if (!isTransientError(error)) {
              rejectOnce(error);
            }
          }
        }
      };

      // Listen for Pusher notifications
      pusherHelper.on(pusherHelper.eventTypes.WEB_PUSH_NOTIFICATION, handlePusherNotification);
      pusherListenerAdded = true;

      // Periodically check status so a missed Pusher event does not leave the user waiting forever.
      pollIntervalId = setInterval(async () => {
        if (pollInFlight || settled) {
          return;
        }
        pollInFlight = true;
        try {
          await checkRequestStatus('status poll', false);
        } finally {
          pollInFlight = false;
        }
      }, POLL_INTERVAL_MS);

      // Store cleanup function in closure variable
      pusherCleanup = cleanup;
    });

    console.log('DeepCut API: Waiting for Pusher notification with status polling fallback');

    try {
      const result = await pusherPromise;

      // Cleanup Pusher listener if it exists
      if (pusherCleanup) {
        pusherCleanup();
      }

      return result;
    } catch (error) {
      // Cleanup Pusher listener on error
      if (pusherCleanup) {
        pusherCleanup();
      }
      throw error;
    }
  }

  /**
   * Analyzes transcript and returns editing suggestions
   * @param {TranscriptAnalysisRequest} requestData - Analysis request data
   * @returns {Promise<AnalysisResults>}
   */
  async analyzeTranscript(requestData) {
    try {
      this._validateAnalysisRequest(requestData);

      const api = TrebbleClientAPIHelper.getInstance();
      const transformedData = this._transformRequestData(requestData);

      console.log('DeepCut API: Making analyzeTranscript request with referenceRequestUID:', this._currentReferenceRequestUID);

      const result = await api.preprocessTransriptionForDeepcut(
        transformedData.transcript,
        transformedData.selectedIntent,
        transformedData.userDirectives,
        transformedData.platformType,
        transformedData.contentType,
        transformedData.customDirectives,
        transformedData.probingQuestionAnswers,
        transformedData.userFlow,
        this._currentReferenceRequestUID,
        transformedData.transcriptionId,
        transformedData.projectId,
        transformedData.referenceProjectId,
        transformedData.isEditLikeMeMode,
        transformedData.locale
      );

      // Validate response structure
      if (!result || typeof result !== 'object') {
        throw new Error(window.getI18n(ti18n, 'INVALID_RESPONSE'));
      }

      // Check if the request is being processed asynchronously
      if (result.status === 'PROCESSING' && result.requestId) {
        console.log('DeepCut API: Request is being processed asynchronously, starting to poll...');

        // Store referenceRequestUID if present
        if (result.referenceRequestUID) {
          this.setReferenceRequestUID(result.referenceRequestUID);
        }

        // Poll for completion
        const completedResult = await this._pollForCompletion(result.requestId);

        // Return the completed result, preserving referenceEditingPatterns if present
        const finalResult = completedResult.analysisResults || completedResult;
        // Check all possible locations for referenceEditingPatterns
        const refPatterns = completedResult.referenceEditingPatterns
          || (completedResult.body && completedResult.body.referenceEditingPatterns)
          || null;
        if (refPatterns && typeof refPatterns === 'object' && Object.keys(refPatterns).length > 0) {
          finalResult.referenceEditingPatterns = refPatterns;
          console.log('DeepCut API: Preserved referenceEditingPatterns with keys:', Object.keys(refPatterns).join(', '));
        }
        // Also preserve the S3 URL for patterns (stable reference even if object gets lost)
        const refPatternsUrl = completedResult.referenceEditingPatternsUrl
          || (completedResult.body && completedResult.body.referenceEditingPatternsUrl)
          || null;
        if (refPatternsUrl) {
          finalResult.referenceEditingPatternsUrl = refPatternsUrl;
          console.log('DeepCut API: Preserved referenceEditingPatternsUrl:', refPatternsUrl);
        }
        return finalResult;
      }

      // Legacy synchronous response handling (for backward compatibility)
      // Extract and store referenceRequestUID from response if present
      if (result.referenceRequestUID) {
        console.log('DeepCut API: Received new referenceRequestUID:', result.referenceRequestUID);
        this.setReferenceRequestUID(result.referenceRequestUID);
      } else {
        console.log('DeepCut API: No referenceRequestUID in response');
      }

      // Extract analysisResults from the response if it's nested
      if (result.analysisResults) {
        return result.analysisResults;
      }

      // Return result directly if it's already in the expected format
      return result;
    } catch (error) {
      console.error('Analysis request failed:', error);

      // Map known errors to user-friendly messages
      if (error.message && (error.message.includes('network') || error.message.includes('Network'))) {
        throw new Error(window.getI18n(ti18n, 'NETWORK_ERROR'));
      }

      if (error.message && (error.message.includes('server') || error.message.includes('Server'))) {
        throw new Error(window.getI18n(ti18n, 'SERVER_ERROR'));
      }

      if (error.message && (error.message.includes('timeout') || error.message.includes('Timeout'))) {
        throw new Error(window.getI18n(ti18n, 'TIMEOUT_ERROR'));
      }

      // Re-throw validation errors as-is
      if (error.message && (error.message.includes('required') || error.message.includes('must be') || error.message.includes('cannot be'))) {
        throw error;
      }

      // Re-throw TrebbleClientAPI errors as-is if they're already user-friendly
      if (error.message && error.message.length < 200) {
        throw error;
      }

      // Generic error for unknown issues
      throw new Error(window.getI18n(ti18n, 'ANALYSIS_FAILED'));
    }
  }

  /**
   * ELM v2: Synthesize reference patterns with target transcript.
   * Calls the ELM Producer Lambda directly via Function URL.
   * Returns { synthesisResult, referenceEditingPatterns } or throws on failure.
   *
   * @param {Object} referenceEditingPatterns - From pattern extractor
   * @param {string} transcript - Clean target transcript (no CIDs)
   * @param {Array} [userAnswers] - Style choice answers (empty on first call)
   * @param {Object} [previousPlan] - Previous synthesis output (null on first call)
   * @param {number} [roundNumber] - 1 = initial, 2 = refinement
   * @returns {Promise<Object>} { synthesisResult, referenceEditingPatterns }
   */
  async synthesizeWithReference(referenceEditingPatterns, transcript, userAnswers = [], previousPlan = null, roundNumber = 1) {
    const ELM_PRODUCER_URL = 'https://n22s2afbyeqkzhl3k7smbrxn5i0zaoxl.lambda-url.us-east-1.on.aws/';

    console.log('DeepCut API: Calling synthesize-with-reference, round', roundNumber);

    const payload = {
      action: 'synthesize-with-reference',
      referenceEditingPatterns,
      transcript,
      userAnswers,
      previousPlan,
      roundNumber,
      locale: navigator.language || 'en',
    };

    let response;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 35000);
    try {
      response = await fetch(ELM_PRODUCER_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
    } catch (err) {
      if (err.name === 'AbortError') {
        console.warn('DeepCut API: Client timeout after 35s, falling back');
        return { synthesisTimedOut: true, referenceEditingPatterns };
      }
      throw err;
    } finally {
      clearTimeout(timeoutId);
    }

    if (!response.ok) {
      throw new Error(`Synthesis request failed: HTTP ${response.status}`);
    }

    const result = await response.json();
    const body = typeof result.body === 'string' ? JSON.parse(result.body) : (result.body || result);

    if (body.synthesisTimedOut) {
      console.warn('DeepCut API: Synthesis timed out, falling back');
      return { synthesisTimedOut: true, referenceEditingPatterns };
    }

    if (!body.synthesisResult) {
      throw new Error('Synthesis returned no result');
    }

    console.log('DeepCut API: Synthesis complete —', body.synthesisResult?.plan?.length || 0, 'plan items,', body.synthesisResult?.styleChoices?.length || 0, 'style choices');
    return body;
  }

  /**
   * Updates analysis based on user feedback
   * @param {TranscriptAnalysisRequest} originalRequest - Original analysis request
   * @param {UserFeedback} userFeedback - User feedback and modifications
   * @returns {Promise<AnalysisResults>}
   */
  async updateAnalysis(originalRequest, userFeedback) {
    try {
      // Convert questionAnswers array to probingQuestionAnswers object if needed
      let feedbackQuestionAnswers = {};
      if (userFeedback.questionAnswers && Array.isArray(userFeedback.questionAnswers)) {
        userFeedback.questionAnswers.forEach(qa => {
          if (qa.questionId && qa.answer) {
            feedbackQuestionAnswers[qa.questionId] = qa.answer;
          }
        });
      } else if (userFeedback.probingQuestionAnswers) {
        feedbackQuestionAnswers = userFeedback.probingQuestionAnswers;
      }

      // Merge user feedback with original request
      const updatedRequest = {
        ...originalRequest,
        userDirectives: [
          ...originalRequest.userDirectives,
          ...(userFeedback.newDirectives || [])
        ],
        selectedIntent: userFeedback.selectedIntent || originalRequest.selectedIntent,
        targetPlatform: userFeedback.targetPlatform || originalRequest.targetPlatform,
        selectedContentType: userFeedback.selectedContentType || originalRequest.selectedContentType,
        customDirectives: userFeedback.customDirectives || originalRequest.customDirectives,
        probingQuestionAnswers: {
          ...originalRequest.probingQuestionAnswers,
          ...feedbackQuestionAnswers
        },
        userFlow: {
          ...originalRequest.userFlow,
          ...userFeedback.userFlow
        }
      };

      // Add user feedback to the request
      updatedRequest.userFeedback = userFeedback;

      return await this.analyzeTranscript(updatedRequest);
    } catch (error) {
      console.error('Update analysis request failed:', error);
      throw error;
    }
  }

  /**
   * Updates an editing plan based on user feedback
   * @param {Object} originalPlan - The original editing plan
   * @param {string} userAnswer - User's feedback/answer
   * @returns {Promise<Object>} Updated plan
   */
  async updateEditingPlan(originalPlan, userAnswer) {
    try {
      const api = TrebbleClientAPIHelper.getInstance();
      
      console.log('DeepCut API: Making updateEditingPlan request with referenceRequestUID:', this._currentReferenceRequestUID);
      const result = await api.updateEditingPlan(originalPlan, userAnswer, this._currentReferenceRequestUID);
      
      if (!result || typeof result !== 'object') {
        throw new Error(window.getI18n(ti18n, 'INVALID_RESPONSE'));
      }

      // Extract and store referenceRequestUID from response if present
      if (result.referenceRequestUID) {
        this.setReferenceRequestUID(result.referenceRequestUID);
      }

      // Extract nested results if present
      return result.updatedPlan || result.analysisResults || result;
    } catch (error) {
      console.error('Update editing plan request failed:', error);
      throw error;
    }
  }

  /**
   * Executes a finalized editing plan
   * @param {Object} executePlanData - Plan execution data
   * @returns {Promise<Object>} Execution result
   */
  async executeEditingPlan(executePlanData) {
    try {
      const api = TrebbleClientAPIHelper.getInstance();
      
      console.log('DeepCut API: Making executeEditingPlan request with referenceRequestUID:', this._currentReferenceRequestUID);
      const result = await api.executeEditingPlan(executePlanData, this._currentReferenceRequestUID);
      
      if (!result || typeof result !== 'object') {
        throw new Error(window.getI18n(ti18n, 'INVALID_RESPONSE'));
      }

      // Extract and store referenceRequestUID from response if present
      if (result.referenceRequestUID) {
        this.setReferenceRequestUID(result.referenceRequestUID);
      }

      // Extract nested results if present
      return result.executionResult || result.analysisResults || result;
    } catch (error) {
      console.error('Execute editing plan request failed:', error);
      throw error;
    }
  }

  /**
   * Starts the editing process
   * @returns {Promise<Object>} Start editing result
   */
  async startEditing() {
    try {
      const api = TrebbleClientAPIHelper.getInstance();
      
      console.log('DeepCut API: Making startEditing request with referenceRequestUID:', this._currentReferenceRequestUID);
      const result = await api.startEditing(this._currentReferenceRequestUID);
      
      if (!result || typeof result !== 'object') {
        throw new Error(window.getI18n(ti18n, 'INVALID_RESPONSE'));
      }

      // Extract and store referenceRequestUID from response if present
      if (result.referenceRequestUID) {
        this.setReferenceRequestUID(result.referenceRequestUID);
      }

      return result;
    } catch (error) {
      console.error('Start editing request failed:', error);
      throw error;
    }
  }

  /**
   * Classify audio files for addition patterns.
   * Calls the backend /batchClassifyAudio endpoint which checks cache first.
   * @param {Array<{audioUrl: string, filename?: string}>} audioFiles
   * @returns {Promise<Array<{audioUrl: string, classification: Object, success: boolean}>>}
   */
  async classifyAdditionPatterns(additionPatterns) {
    if (!additionPatterns || additionPatterns.length === 0) return [];

    const audioFiles = additionPatterns
      .filter(p => p.fileInfo?.audioUrl)
      .map(p => ({
        audioUrl: p.fileInfo.audioUrl,
        filename: p.fileInfo.title || null,
      }));

    if (audioFiles.length === 0) return [];

    try {
      const api = TrebbleClientAPIHelper.getInstance();
      const results = await api.batchClassifyAudio(audioFiles);
      console.log('DeepCut API: Audio classification results:', results?.length || 0, 'files');
      return results || [];
    } catch (error) {
      console.warn('DeepCut API: Audio classification failed (non-fatal):', error.message);
      return [];
    }
  }

  /**
   * Health check for the API service
   * @returns {Promise<boolean>}
   */
  async healthCheck() {
    try {
      const api = TrebbleClientAPIHelper.getInstance();
      
      // Simple test with minimal data to check if the endpoint is working
      const testResult = await api.preprocessTransriptionForDeepcut(
        'test',
        'test_intent',
        [],
        'web',
        'conversation',
        '',
        {},
        {},
        null // Don't use current referenceRequestUID for health check
      );
      
      // Health check passes if we get any response (nested or direct)
      const extractedResult = testResult?.analysisResults || testResult;
      
      return true;
    } catch (error) {
      console.warn('Health check failed:', error.message);
      return false;
    }
  }
}

// Create singleton instance
const deepCutApi = new DeepCutApiService();

export default deepCutApi;
